import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { NotebookDto } from '@/types/api';
import { EMPTY_FILTER_SORT, type FilterSortState } from '@/components/filter/filterModel';
import { EMPTY_GROUP, type GroupState } from '@/components/group/groupModel';

export interface Cell {
  readonly id: string;
  sql: string;
  /**
   * Authored Filter & Sort state. Lives here rather than in the result store so
   * it survives a reload with the notebook, the way the SQL does.
   */
  filterSort?: FilterSortState;
  /** Authored group-by state, persisted with the cell. */
  group?: GroupState;
}

export interface Notebook {
  readonly id: string;
  name: string;
  cells: Cell[];
  updatedAt: string;
}

interface NotebookState {
  notebooks: Notebook[];
  activeNotebookId: string;
  activeCellId: string | null;

  activeNotebook: () => Notebook;
  setActiveCell: (cellId: string) => void;
  setCellSql: (cellId: string, sql: string) => void;
  setCellFilterSort: (cellId: string, filterSort: FilterSortState) => void;
  setCellGroup: (cellId: string, group: GroupState) => void;
  addCell: (afterCellId?: string) => string;
  deleteCell: (cellId: string) => void;
  duplicateCell: (cellId: string) => string | null;
  moveCell: (cellId: string, direction: -1 | 1) => void;
  renameNotebook: (name: string) => void;
  createNotebook: () => string;
  selectNotebook: (id: string) => void;
  renameNotebookById: (id: string, name: string) => void;
  deleteNotebook: (id: string) => void;
  /** Re-identifies a notebook whose id the server rejected. Content is kept. */
  reassignNotebookId: (oldId: string, newId: string) => void;
  /** Replaces local state with the server's copy on first load. */
  hydrateFromServer: (notebooks: readonly NotebookDto[]) => void;
  toDto: () => NotebookDto;
}

let counter = 0;
export function newId(prefix: string): string {
  counter += 1;
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${random}${counter.toString(36)}`;
}

const STARTER_SQL = `-- ⌘↵ to run. ⌘K for the command palette.
SELECT
  YEAR,
  count(*)            AS patients,
  avg(age)            AS mean_age,
  sum(total_charges)  AS charges
FROM raqeem.patient_yearly
GROUP BY YEAR
ORDER BY YEAR;`;

function starterNotebook(): Notebook {
  const cell: Cell = { id: newId('cell'), sql: STARTER_SQL, filterSort: EMPTY_FILTER_SORT, group: EMPTY_GROUP };
  return {
    id: newId('nb'),
    name: 'Untitled notebook',
    cells: [cell],
    updatedAt: new Date().toISOString(),
  };
}

function touch(notebook: Notebook): Notebook {
  return { ...notebook, updatedAt: new Date().toISOString() };
}

export const useNotebookStore = create<NotebookState>()(
  persist(
    (set, get) => {
      const initial = starterNotebook();

      const updateActive = (mutate: (notebook: Notebook) => Notebook): void => {
        set((state) => ({
          notebooks: state.notebooks.map((notebook) =>
            notebook.id === state.activeNotebookId ? touch(mutate(notebook)) : notebook,
          ),
        }));
      };

      return {
        notebooks: [initial],
        activeNotebookId: initial.id,
        activeCellId: initial.cells[0]?.id ?? null,

        activeNotebook: () => {
          const state = get();
          return (
            state.notebooks.find((notebook) => notebook.id === state.activeNotebookId) ??
            state.notebooks[0] ??
            starterNotebook()
          );
        },

        setActiveCell: (activeCellId) => set({ activeCellId }),

        setCellSql: (cellId, sql) =>
          updateActive((notebook) => ({
            ...notebook,
            cells: notebook.cells.map((cell) => (cell.id === cellId ? { ...cell, sql } : cell)),
          })),

        setCellFilterSort: (cellId, filterSort) =>
          updateActive((notebook) => ({
            ...notebook,
            cells: notebook.cells.map((cell) =>
              cell.id === cellId ? { ...cell, filterSort } : cell,
            ),
          })),

        setCellGroup: (cellId, group) =>
          updateActive((notebook) => ({
            ...notebook,
            cells: notebook.cells.map((cell) => (cell.id === cellId ? { ...cell, group } : cell)),
          })),

        addCell: (afterCellId) => {
          const id = newId('cell');
          updateActive((notebook) => {
            const index = afterCellId
              ? notebook.cells.findIndex((cell) => cell.id === afterCellId)
              : notebook.cells.length - 1;
            const cells = [...notebook.cells];
            cells.splice(index + 1, 0, { id, sql: '' });
            return { ...notebook, cells };
          });
          set({ activeCellId: id });
          return id;
        },

        deleteCell: (cellId) => {
          const notebook = get().activeNotebook();
          // A notebook always has at least one cell; clear the last one instead.
          if (notebook.cells.length <= 1) {
            updateActive((nb) => ({
              ...nb,
              cells: nb.cells.map((cell) => ({ ...cell, sql: '' })),
            }));
            return;
          }
          const index = notebook.cells.findIndex((cell) => cell.id === cellId);
          const nextActive =
            notebook.cells[index + 1]?.id ?? notebook.cells[index - 1]?.id ?? null;
          updateActive((nb) => ({
            ...nb,
            cells: nb.cells.filter((cell) => cell.id !== cellId),
          }));
          if (get().activeCellId === cellId) set({ activeCellId: nextActive });
        },

        duplicateCell: (cellId) => {
          const notebook = get().activeNotebook();
          const source = notebook.cells.find((cell) => cell.id === cellId);
          if (!source) return null;
          const id = newId('cell');
          updateActive((nb) => {
            const index = nb.cells.findIndex((cell) => cell.id === cellId);
            const cells = [...nb.cells];
            cells.splice(index + 1, 0, { id, sql: source.sql });
            return { ...nb, cells };
          });
          set({ activeCellId: id });
          return id;
        },

        moveCell: (cellId, direction) =>
          updateActive((notebook) => {
            const index = notebook.cells.findIndex((cell) => cell.id === cellId);
            const target = index + direction;
            if (index === -1 || target < 0 || target >= notebook.cells.length) return notebook;
            const cells = [...notebook.cells];
            const moved = cells[index];
            const displaced = cells[target];
            if (!moved || !displaced) return notebook;
            cells[index] = displaced;
            cells[target] = moved;
            return { ...notebook, cells };
          }),

        renameNotebook: (name) => updateActive((notebook) => ({ ...notebook, name })),

        createNotebook: () => {
          const notebook = starterNotebook();
          set((state) => ({
            notebooks: [...state.notebooks, notebook],
            activeNotebookId: notebook.id,
            activeCellId: notebook.cells[0]?.id ?? null,
          }));
          return notebook.id;
        },

        selectNotebook: (id) => {
          const notebook = get().notebooks.find((candidate) => candidate.id === id);
          if (!notebook) return;
          set({ activeNotebookId: id, activeCellId: notebook.cells[0]?.id ?? null });
        },

        renameNotebookById: (id, name) =>
          set((state) => ({
            notebooks: state.notebooks.map((notebook) =>
              notebook.id === id ? touch({ ...notebook, name }) : notebook,
            ),
          })),

        deleteNotebook: (id) => {
          const state = get();
          // Never leave the workspace with nothing open.
          if (state.notebooks.length <= 1) return;
          const remaining = state.notebooks.filter((notebook) => notebook.id !== id);
          const nextActive =
            state.activeNotebookId === id
              ? (remaining[0]?.id ?? state.activeNotebookId)
              : state.activeNotebookId;
          const nextCell =
            state.activeNotebookId === id
              ? (remaining[0]?.cells[0]?.id ?? null)
              : state.activeCellId;
          set({ notebooks: remaining, activeNotebookId: nextActive, activeCellId: nextCell });
        },

        reassignNotebookId: (oldId, nextId) =>
          set((state) => ({
            notebooks: state.notebooks.map((notebook) =>
              // Deliberately does not touch updatedAt: bumping it would
              // retrigger the debounced save and loop.
              notebook.id === oldId ? { ...notebook, id: nextId } : notebook,
            ),
            activeNotebookId:
              state.activeNotebookId === oldId ? nextId : state.activeNotebookId,
          })),

        hydrateFromServer: (dtos) => {
          if (dtos.length === 0) return;
          set((state) => {
            const byId = new Map(state.notebooks.map((notebook) => [notebook.id, notebook]));
            for (const dto of dtos) {
              const local = byId.get(dto.id);
              // Local edits are newer than the server copy in the common case,
              // so only adopt the server version when it is strictly newer.
              if (local && Date.parse(local.updatedAt) >= Date.parse(dto.updatedAt)) continue;
              byId.set(dto.id, {
                id: dto.id,
                name: dto.name,
                updatedAt: dto.updatedAt,
                cells: dto.cells.map((cell) => ({ id: cell.id, sql: cell.sql })),
              });
            }
            return { notebooks: [...byId.values()] };
          });
        },

        toDto: () => {
          const notebook = get().activeNotebook();
          return {
            id: notebook.id,
            name: notebook.name,
            updatedAt: notebook.updatedAt,
            cells: notebook.cells.map((cell) => ({ id: cell.id, sql: cell.sql })),
          };
        },
      };
    },
    {
      name: 'duckdb.notebooks',
      partialize: (state) => ({
        notebooks: state.notebooks,
        activeNotebookId: state.activeNotebookId,
        activeCellId: state.activeCellId,
      }),
    },
  ),
);
